import { Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { fetchAffiliates, fetchCallRules, fetchCallZips, fetchCampaigns, fetchContracts, fetchLeadFilters, fetchLeadRules, fetchVerticals } from '../../../utils/leadspedia/api';
import { activeVerticals, VerticalOffer } from '../../../data/lpdata';
import { LpCampaign } from '../../../components/campaign-list/campaign-list.component';
import { Rule } from '../../dashboard/dashboard.component';
import { utils as XLSXUtils, writeFile } from 'xlsx';
import DescriptionIcon from '@mui/icons-material/Description';
import { fetchUsers, LPUser } from '../../../utils/firebase/firebase.utils';
import { on } from 'events';

interface ContractZip {
    contractID: number;
    contractName: string;
    advertiserName: string;
    contractStatus: string;
    excludeZipCodes: string;
    includeZipCodes: string;
}

const defaultReport2FormFields = {
    report2AffiliateId: '',
    report2AdvertiserId: '',
    report2VerticalId: '',
    report2OfferId: '',
}

const Report2: React.FC = () => {
    const [users, setUsers] = useState<LPUser[]>([]);
    const [affiliates, setAffiliates] = useState<any[]>([])
    const [lpVerticals, setLpVerticals] = useState<any[] | null>([]);
    const [lpCampaigns, setLpCampaigns] = useState<LpCampaign[] | null>(null);
    const [contracts, setContracts] = useState<any[]>([]);
    const [lpType, setLpType] = useState<string>('');
    const [affiliateName, setAffiliateName] = useState<string>('');
    const [matchedZips, setMatchedZips] = useState<ContractZip[]>([]);
    const [offers, setOffers] = useState<VerticalOffer[]>([]);
    const [report2FormFields, setReport2FormFields] = useState(defaultReport2FormFields)
    const { report2AffiliateId, report2AdvertiserId, report2VerticalId, report2OfferId } = report2FormFields
    const [isGenerating, setIsGenerating] = useState<boolean>(false);

    function getUsers() {
        const userList = fetchUsers();
        userList.then(users => {
            const sortedUsers = users.sort((a: LPUser, b: LPUser) =>
                a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase())
            );
            setUsers(sortedUsers);
        }).catch(error => {
            console.error('Error fetching users:', error);
        });
    }

    const getAffiliates = () => {
        const affList = fetchAffiliates()
        affList.then((response) => {
            setAffiliates(response)
        }).catch((error) => {
            console.error('Error fetching affiliates:', error);
        })
    }

    const fetchAndSetVerticals = async () => {
        await fetchVerticals()
            .then((verticals: any) => {
                setLpVerticals(verticals);
            })
            .catch((error: any) => {
                console.error('Error fetching verticals:', error);
            });
    };

    const handleReport2Change = async (event: any) => {
        // Reset the campaigns and offers when changing affiliate or vertical
        setLpCampaigns(null);
        setOffers([]);
        setContracts([]);
        setMatchedZips([]);
        console.log('Report 2 form fields:', report2FormFields);

        let affiliateId = report2AffiliateId;
        let verticalId = report2VerticalId;
        const { name, value } = event.target
        if (name === 'report2AffiliateId') {
            affiliateId = value;
            setReport2FormFields({ ...report2FormFields, report2AffiliateId: affiliateId, report2OfferId: '' });
            setAffiliateName(affiliates.find(aff => aff.affiliateID === affiliateId)?.affiliateName);
        } else if (name === 'report2VerticalId') {
            verticalId = value;
            setReport2FormFields({ ...report2FormFields, report2VerticalId: verticalId, report2OfferId: '' });
        } else if (name === 'report2AdvertiserId') {
            setReport2FormFields({ ...report2FormFields, report2AdvertiserId: value, report2OfferId: '' });
        }

        if (affiliateId && verticalId) {
            const campaigns: LpCampaign[] = await fetchCampaigns(verticalId, undefined, 0, 1000, parseInt(affiliateId));
            console.log('Fetched campaigns:', campaigns);
            if (campaigns.length > 0) {
                const offerList = Array.from(new Map(
                    campaigns.map(campaign => [
                        campaign.offerID,
                        {
                            id: campaign.offerID,
                            name: campaign.offerName
                        }
                    ])
                ).values());
                setOffers(offerList);
            }
            setLpCampaigns(campaigns);
        }
    }

    const handleReport2OfferChange = async (event: any) => {
        const { name, value } = event.target;
        setReport2FormFields({ ...report2FormFields, [name]: value });
        setMatchedZips([]);
        setContracts([]);
        const selectedOfferId = value;
        const offerName = offers.find(offer => offer.id === selectedOfferId)?.name;
        console.log('Selected offer name:', offerName);
        let type = '';
        if (offerName?.includes('Leads')) {
            type = 'leads';
        } else {
            type = 'calls';
        }
        setLpType(type);

        let advertiserID = undefined;
        if (report2AdvertiserId && report2AdvertiserId !== '') {
            advertiserID = report2AdvertiserId;
        }   

        const verticalContracts = await fetchContracts(type, report2VerticalId, advertiserID);
        console.log('Fetched contracts:', verticalContracts);
        if (verticalContracts && verticalContracts.length > 0) {
            // get contracts that have the same name as offer
            let filteredContracts = verticalContracts.filter((contract: any) => contract.contractName === offerName);
            if (type === 'leads') {
                // Filter out contracts that have 'Block' rule
                const rules = await fetchLeadRules(advertiserID, report2AffiliateId);
                console.log('Fetched lead rules:', rules);
                // Filter out contracts that have 'Block' rule
                filteredContracts = filteredContracts.filter((contract: any) => {
                    // Remove contracts that exist in rules array
                    return !rules.some((rule: Rule) =>
                        rule.contractID === parseInt(contract.contractID) &&
                        rule.rule === 'Block'
                    );
                });
            } else if (type === 'calls') {
                const rules = await fetchCallRules(advertiserID, report2AffiliateId);
                console.log('Fetched call rules:', rules);
                // Filter out contracts that have 'Block' rule                
                filteredContracts = filteredContracts.filter((contract: any) => {
                    // Remove contracts that exist in rules array
                    return !rules.some((rule: Rule) =>
                        rule.contractID === parseInt(contract.contractID) &&
                        rule.rule === 'Block'
                    );
                });
            }

            console.log('Filtered contracts:', filteredContracts);
            setContracts(filteredContracts);
        }
    }

    const generateReport2 = async () => {
        //USE Calls by Brand for Calls affiliate
        //USE Blue Ink Digital for Leads affiliate
        setIsGenerating(true);
        setMatchedZips([]);
        if (contracts && contracts.length > 0) {
            let matchingZips;
            
            // Get zip data based on type
            const zipData = await (lpType === 'leads' 
                ? fetchLeadFilters().then(filters => filters.filter((f:any) => f.uniqueName === 'zip_code'))
                : fetchCallZips()
            );

            // Find matching contracts and enrich with advertiser info
            matchingZips = zipData
                .filter((zip: any) => contracts.some(contract => 
                    parseInt(contract.contractID) === parseInt(zip.contractID)
                ))
                .map((zip: any) => {
                    const matchedContract = contracts.find(contract => 
                        parseInt(contract.contractID) === parseInt(zip.contractID)
                    );
                    
                    return {
                        ...zip,
                        advertiserName: matchedContract?.buyerName,
                        contractName: matchedContract?.contractName,
                        // For leads, set include/exclude based on operator
                        ...(lpType === 'leads' && {
                            includeZipCodes: zip.operator === 'Equals' ? zip.compareValues : '',
                            excludeZipCodes: zip.operator === 'Not Equals' ? zip.compareValues : ''
                        })
                    };
                });

            console.log('Matching contracts with zips:', matchingZips);
            
            setMatchedZips(matchingZips);
        }
        setIsGenerating(false);
    }

    const exportToExcel = () => {
        if (matchedZips.length > 0) {
            // merge all includedZips into one string and remove the duplicates
            const uniqueIncludedZips = Array.from(new Set(
                matchedZips
                    .flatMap(zip => zip.includeZipCodes.split(','))
                    .map(zip => zip.trim())
                    .filter(zip => zip !== '')
            ));

            // Create a worksheet with zip codes
            const worksheet = XLSXUtils.json_to_sheet(
                uniqueIncludedZips.map(zip => ({ ZipCode: zip }))
            );

            // Create a workbook and append the worksheet
            const workbook = XLSXUtils.book_new();
            XLSXUtils.book_append_sheet(workbook, worksheet, 'Zip Codes');

            // Generate the Excel file
            writeFile(workbook, `${affiliateName}_${lpType}_zip_codes.xlsx`);
        }
    }

    useEffect(() => {
        getUsers();
        getAffiliates();
        fetchAndSetVerticals();
    }, []);

    return (
        <React.Fragment>
            <h1>Report 2 - Advertiser Zips</h1>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 2, md: 4 }}
                sx={{ textAlign: 'left', p: 2, backgroundColor: 'white' }}
            >
                <FormControl size='small' fullWidth>
                    <InputLabel id="report2-affiliate">Affiliate</InputLabel>
                    <Select
                        labelId="report2-affiliate"
                        id="report2-affiliate-select"
                        name="report2AffiliateId"
                        value={report2AffiliateId}
                        label="Affiliate"
                        onChange={handleReport2Change}
                    >
                        {affiliates.map(affiliate => (
                            <MenuItem key={affiliate.affiliateID} value={affiliate.affiliateID}>{affiliate.affiliateName}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size='small' fullWidth>
                    <InputLabel id="report2-advertiser">Advertiser</InputLabel>
                    <Select
                        labelId="report2-advertiser"
                        id="report2-advertiser-select"
                        name="report2AdvertiserId"
                        value={report2AdvertiserId}
                        label="Advertiser"
                        onChange={handleReport2Change}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {users.map(user => (
                            <MenuItem key={user.id} value={user.advertiserId}>{user.displayName}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size='small' fullWidth>
                    <InputLabel id="report2-vertical">Vertical</InputLabel>
                    <Select
                        labelId="report2-vertical"
                        id="report2-vertical-select"
                        name="report2VerticalId"
                        value={report2VerticalId}
                        label="Vertical"
                        onChange={handleReport2Change}
                    >
                        {
                            lpVerticals && lpVerticals.length > 0 &&
                            lpVerticals
                                .filter(ver => activeVerticals.includes(ver.verticalID))
                                .map(ver => (
                                    <MenuItem key={ver.verticalID} value={ver.verticalID}>{ver.verticalName}</MenuItem>
                                ))
                        }
                    </Select>
                </FormControl>
                <FormControl size='small' fullWidth>
                    <InputLabel id="report2-offer">Offer</InputLabel>
                    <Select
                        labelId="report2-offer"
                        id="report2-offer-select"
                        name="report2OfferId"
                        value={report2OfferId}
                        label="Offer"
                        onChange={handleReport2OfferChange}
                    >
                        {offers.map(offer => (
                            <MenuItem key={offer.id} value={offer.id}>{offer.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <div>
                    <Button
                        variant="contained"
                        onClick={generateReport2}
                        size="medium"
                        disabled={report2AffiliateId === '' || report2VerticalId === '' || report2OfferId === '' || isGenerating}
                    >
                        {isGenerating ? 'Generating...' : 'Generate'}
                    </Button>
                </div>
            </Stack>

            <Stack>
                {matchedZips.length > 0 ? (
                    <>
                    <Box sx={{ display: 'flex', justifyContent: 'end', p: 2, backgroundColor: 'white', borderTop: '1px solid #e0e0e0' }}>
                        <Button
                            variant="contained"
                            onClick={exportToExcel}
                            size="medium"
                            startIcon={<DescriptionIcon />}
                        >
                            Export to Excel
                        </Button>
                    </Box>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#e5e5e5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Affiliate</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Advertiser</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Contract Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Excluded Zips</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Included Zips</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {matchedZips.map((zip) => (
                                    <TableRow
                                        key={zip.contractID}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell>{affiliateName}</TableCell>
                                        <TableCell>{zip.advertiserName}</TableCell>
                                        <TableCell>{zip.contractName}</TableCell>
                                        <TableCell
                                            sx={{
                                                maxWidth: 300,
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word'
                                            }}
                                        >{zip.excludeZipCodes}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                maxWidth: 300,
                                                whiteSpace: 'normal',
                                                wordWrap: 'break-word'
                                            }}
                                        >{zip.includeZipCodes}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    </>
                ) : isGenerating ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <CircularProgress />
                        <p>Generating report...</p>
                    </div>
                ) : (
                    <Typography sx={{ backgroundColor: 'white', p: 2 }}>
                        No matching zip codes found.
                    </Typography>
                )}
            </Stack>
        </React.Fragment>
    );
};

export default Report2;